import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { RootState } from "../../store/store";
import {
  cancelBookingByDatesApi,
  getAdByIdApi,
  getBookingsByUserApi,
  saveBookingByAdsApi,
} from "../../api/adsApi";
import { getLocalizedText } from "../../features/ads/i18nFields";
import ShimmerImage from "../../components/ShimmerImage/ShimmerImage";
import { AD_IMAGE_PLACEHOLDER } from "../../utils/adImagePlaceholder";
import styles from "./MyBookings.module.css";

type RowStatus = "active" | "cancelled" | "locked" | "validated";

type BookingDateItem = {
  date: string;
  booked: boolean | null;
  priceByDay: number | null;
};

type BookingGroup = {
  id: string;
  adId: string;
  bookingGroup: string;
  title: string;
  description: string;
  image: string;
  currency: string;
  dates: BookingDateItem[];
  status: RowStatus;
  loading: boolean;
};

type ConfirmAction = {
  groupId: string;
  mode: "cancel" | "rebook";
};

const BOOKINGS_PAGE_LIMIT = 10;
const placeholder = AD_IMAGE_PLACEHOLDER;

const toDateKey = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    const prefixedDate = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    return prefixedDate ? prefixedDate[1] : null;
  }
  if (typeof value === "object") {
    const dateLike = (value as any)?.date ?? (value as any)?.bookingDate ?? (value as any)?.day ?? null;
    return toDateKey(dateLike);
  }
  return null;
};

const toBookedFlag = (value: unknown): boolean | null => {
  if (!value || typeof value !== "object") return null;
  if (!("booked" in (value as Record<string, unknown>))) return null;
  const raw = (value as { booked?: unknown }).booked;
  if (raw === null) return null;
  if (typeof raw === "boolean") return raw;
  return null;
};

const toValidPrice = (value: unknown): number | null => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
};

const getTodayDateKey = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const truncate = (text: string, max = 180): string => {
  const normalized = String(text || "").trim();
  if (!normalized) return "";
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max).trimEnd()}...`;
};

const toPublicPhoto = (value: unknown): string | null => {
  const raw =
    typeof value === "string"
      ? value
      : (value as any)?.url || (value as any)?.name || (value as any)?.path || (value as any)?.src || "";
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!/^https?:\/\//i.test(trimmed) && !trimmed.startsWith("/upload/")) return null;
  return trimmed;
};

const normalizeAd = (payload: any): any => {
  const raw = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const normalizedPhotos = Array.isArray(raw?.photos)
    ? raw.photos.map((photo: any) => toPublicPhoto(photo)).filter(Boolean)
    : [];

  return {
    ...raw,
    id: raw?.id ?? raw?._id,
    title_i18n: raw?.title_i18n || { fr: "", en: "", ar: "" },
    content_i18n: raw?.content_i18n || { fr: "", en: "", ar: "" },
    adress_i18n: raw?.adress_i18n || { fr: raw?.adress || "", en: "", ar: "" },
    photos: normalizedPhotos,
  };
};

const resolveAdId = (booking: any): string => {
  const rawAd = booking?.ads;
  if (typeof rawAd === "string" && rawAd.trim()) return rawAd.trim();
  if (rawAd && typeof rawAd === "object") {
    const objectId = String((rawAd as any)?.id || (rawAd as any)?._id || "").trim();
    if (objectId) return objectId;
  }
  return String(booking?.id_ads || booking?.adId || "").trim();
};

const formatMoney = (amount: number, currency: string, locale: string): string => {
  const normalizedCurrency = String(currency || "EUR").toUpperCase();
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: normalizedCurrency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${normalizedCurrency}`;
  }
};

const MyBookingsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || "fr";
  const authUser = useSelector((state: RootState) => state.auth.user as any);
  const bookingStreamTick = useSelector((state: RootState) => state.auth.bookingStreamTick);
  const userId = String(authUser?.id || authUser?._id || "");

  const [groups, setGroups] = useState<BookingGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadRequestIdRef = useRef(0);

  const getServerDateStatusesByAd = useCallback(
    async (adId: string): Promise<Map<string, RowStatus>> => {
      const response = await getAdByIdApi(adId);
      const responseData: any = (response as any)?.data;
      const adPayload = responseData?.data && typeof responseData.data === "object" ? responseData.data : responseData;
      const bookingDateEntries = Array.isArray((adPayload as any)?.bookingDates)
        ? (adPayload as any).bookingDates
        : [];

      const statusByDate = new Map<string, RowStatus>();
      bookingDateEntries.forEach((dateEntry: any) => {
        const dateKey = toDateKey((dateEntry as any)?.date);
        if (!dateKey) return;
        const reservations = Array.isArray((dateEntry as any)?.reservations)
          ? (dateEntry as any).reservations
          : [];
        const validatedByCurrentUser = reservations.some((reservation: any) => {
          const reservationUserId = String(reservation?.user?.id || reservation?.user?._id || "");
          return Boolean(reservationUserId && reservationUserId === userId && reservation?.booked === true);
        });
        const hasCurrentUserReservation = reservations.some((reservation: any) => {
          const reservationUserId = String(reservation?.user?.id || reservation?.user?._id || "");
          return Boolean(reservationUserId && reservationUserId === userId);
        });
        const validatedByOtherUser = reservations.some((reservation: any) => {
          const reservationUserId = String(reservation?.user?.id || reservation?.user?._id || "");
          return Boolean(reservationUserId && reservationUserId !== userId && reservation?.booked === true);
        });

        if (validatedByCurrentUser) {
          statusByDate.set(dateKey, "validated");
          return;
        }
        if (validatedByOtherUser) {
          statusByDate.set(dateKey, "locked");
          return;
        }
        if (hasCurrentUserReservation) {
          statusByDate.set(dateKey, "active");
        }
      });

      return statusByDate;
    },
    [userId]
  );

  const loadBookings = useCallback(async (options: { silent?: boolean; append?: boolean; cursor?: string | null } = {}) => {
    const { silent = false, append = false, cursor = null } = options;
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;

    if (!userId) {
      setGroups([]);
      setHasMore(false);
      setNextCursor(null);
      return;
    }

    if (append) setLoadingMore(true);
    else if (!silent) {
      setLoading(true);
      setError("");
    }
    try {
      const response = await getBookingsByUserApi(userId, {
        limit: BOOKINGS_PAGE_LIMIT,
        cursor: cursor || undefined,
      });
      const bookings = Array.isArray(response?.data?.data) ? response.data.data : [];
      const groupsByReservation = new Map<string, BookingGroup>();

      bookings.forEach((booking: any) => {
        const ad = normalizeAd(booking?.ads && typeof booking?.ads === "object" ? booking.ads : {});
        const adId = resolveAdId(booking);
        if (!adId) return;
        const dateItems = Array.isArray(booking?.bookingDates) ? booking.bookingDates : [];
        const bookingId = String(booking?._id || "");
        dateItems.forEach((entry: any) => {
          const dateKey = toDateKey((entry as any)?.date ?? entry);
          if (!dateKey) return;
          const bookingGroup = String((entry as any)?.groupe || (entry as any)?.group || `legacy-${bookingId || adId}`);
          const reservationKey = `${adId}-${bookingGroup}`;
          if (!groupsByReservation.has(reservationKey)) {
            const photos = Array.isArray(ad?.photos) ? ad.photos : [];
            const mainImage = toPublicPhoto(photos[0]) || placeholder;
            const title =
              getLocalizedText(ad?.title_i18n || { fr: "", en: "", ar: "" }, locale) ||
              t("myBookings.untitled");
            const description = truncate(
              getLocalizedText(ad?.content_i18n || { fr: "", en: "", ar: "" }, locale)
            );
            groupsByReservation.set(reservationKey, {
              id: reservationKey,
              adId,
              bookingGroup,
              title,
              description,
              image: mainImage,
              currency: String(ad?.currency || "EUR").toUpperCase(),
              dates: [],
              status: "active",
              loading: false,
            });
          }

          const reservationGroup = groupsByReservation.get(reservationKey);
          if (!reservationGroup) return;
          if (!reservationGroup.dates.some((item) => item.date === dateKey)) {
            reservationGroup.dates.push({
              date: dateKey,
              booked: toBookedFlag(entry),
              priceByDay: toValidPrice((entry as any)?.priceByDay ?? booking?.priceByDay),
            });
          }
        });
      });

      const nextGroups = Array.from(groupsByReservation.values())
        .map((group) => {
          const hasValidatedDate = group.dates.some((entry) => entry.booked === true);
          return {
            ...group,
            status: hasValidatedDate ? "validated" : "active",
            dates: [...group.dates].sort((a, b) => b.date.localeCompare(a.date)),
          };
        })
        .sort((a, b) => a.title.localeCompare(b.title, locale));

      const serverStatusesByAdId = new Map<string, Map<string, RowStatus>>();
      await Promise.all(
        Array.from(new Set(nextGroups.map((group) => group.adId))).map(async (adId) => {
          try {
            const statusByDate = await getServerDateStatusesByAd(adId);
            serverStatusesByAdId.set(adId, statusByDate);
          } catch {
            serverStatusesByAdId.set(adId, new Map<string, RowStatus>());
          }
        })
      );

      const nextGroupsWithStatuses = nextGroups.map((group) => {
        const statusByDate = serverStatusesByAdId.get(group.adId) ?? new Map<string, RowStatus>();
        const dateStatuses = group.dates.map((dateItem) => statusByDate.get(dateItem.date) || null);
        const hasValidated = dateStatuses.some((status) => status === "validated");
        const hasLocked = dateStatuses.some((status) => status === "locked");
        const hasActive = dateStatuses.some((status) => status === "active");
        const nextStatus: RowStatus = hasValidated
          ? "validated"
          : hasLocked
            ? "locked"
            : hasActive
              ? "active"
              : "cancelled";
        return {
          ...group,
          status: nextStatus,
        };
      });

      if (loadRequestIdRef.current !== requestId) return;
      setGroups((prev) => {
        if (!append) return nextGroupsWithStatuses;
        const byId = new Map<string, BookingGroup>();
        [...prev, ...nextGroupsWithStatuses].forEach((group) => {
          const existing = byId.get(group.id);
          if (!existing) {
            byId.set(group.id, group);
            return;
          }
          const dateByKey = new Map<string, BookingDateItem>();
          [...existing.dates, ...group.dates].forEach((dateItem) => {
            dateByKey.set(dateItem.date, dateItem);
          });
          byId.set(group.id, {
            ...existing,
            ...group,
            dates: Array.from(dateByKey.values()).sort((a, b) => b.date.localeCompare(a.date)),
          });
        });
        return Array.from(byId.values()).sort((a, b) => a.title.localeCompare(b.title, locale));
      });
      setHasMore(Boolean(response?.data?.hasMore));
      setNextCursor(response?.data?.nextCursor ?? null);
    } catch (err: any) {
      if (loadRequestIdRef.current !== requestId) return;
      if (!append && !silent) {
        setGroups([]);
        setError(err?.response?.data?.message || t("myBookings.loadError"));
      }
    } finally {
      if (loadRequestIdRef.current === requestId && append) {
        setLoadingMore(false);
      } else if (loadRequestIdRef.current === requestId && !silent) {
        setLoading(false);
      }
    }
  }, [getServerDateStatusesByAd, locale, t, userId]);

  useEffect(() => {
    void loadBookings();
  }, [loadBookings]);

  useEffect(() => {
    if (!userId || bookingStreamTick === 0) return;
    void loadBookings({ silent: true, append: false, cursor: null });
  }, [bookingStreamTick, loadBookings, userId]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    if (!hasMore || !nextCursor || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;
        void loadBookings({ append: true, cursor: nextCursor });
      },
      { rootMargin: "240px 0px" }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadBookings, loading, loadingMore, nextCursor]);
  const todayDateKey = useMemo(() => getTodayDateKey(), []);

  const updateGroup = (groupId: string, patch: Partial<BookingGroup>) => {
    setGroups((prev) =>
      prev.map((group) => (group.id === groupId ? { ...group, ...patch } : group))
    );
  };

  const findGroup = (groupId: string) => {
    return groups.find((entry) => entry.id === groupId) || null;
  };

  const requestAction = (groupId: string) => {
    const found = findGroup(groupId);
    if (!found || found.loading || found.status === "locked" || found.status === "validated") return;

    setConfirmAction({
      groupId,
      mode: found.status === "active" ? "cancel" : "rebook",
    });
  };

  const checkIfGroupTakenByAnotherClient = useCallback(
    async (adId: string, dates: string[]): Promise<boolean> => {
      if (!dates.length) return false;
      try {
        const response = await getAdByIdApi(adId);
        const responseData: any = (response as any)?.data;
        const adPayload = responseData?.data && typeof responseData.data === "object" ? responseData.data : responseData;
        const bookingDateEntries = Array.isArray((adPayload as any)?.bookingDates)
          ? (adPayload as any).bookingDates
          : [];
        const dateSet = new Set(dates);

        for (const dateEntry of bookingDateEntries) {
          const dateKey = toDateKey((dateEntry as any)?.date);
          if (!dateKey || !dateSet.has(dateKey)) continue;
          const reservations = Array.isArray((dateEntry as any)?.reservations)
            ? (dateEntry as any).reservations
            : [];
          const alreadyBookedByOther = reservations.some((reservation: any) => {
            const reservationUserId = String(reservation?.user?.id || reservation?.user?._id || "");
            const isBooked = reservation?.booked === true;
            return Boolean(reservationUserId && reservationUserId !== userId && isBooked);
          });
          if (alreadyBookedByOther) return true;
        }
      } catch {
        return false;
      }
      return false;
    },
    [userId]
  );

  const executeConfirmedAction = async () => {
    if (!confirmAction) return;
    const group = findGroup(confirmAction.groupId);
    if (!group) return;
    const groupDates = group.dates.map((entry) => entry.date);
    updateGroup(group.id, { loading: true });
    setError("");

    try {
      if (confirmAction.mode === "cancel") {
        await cancelBookingByDatesApi({
          id_user: userId,
          id_ads: group.adId,
          groupe: group.bookingGroup,
        });
        setGroups((prev) => prev.filter((entry) => entry.id !== group.id));
        toast.success(t("myBookings.cancelSuccess"));
      } else {
        const takenByOther = await checkIfGroupTakenByAnotherClient(group.adId, groupDates);
        if (takenByOther) {
          updateGroup(group.id, { status: "locked", loading: false });
          setConfirmAction(null);
          return;
        }

        await saveBookingByAdsApi({
          id_ads: group.adId,
          id_user: userId,
          bookingDates: groupDates,
          groupe: group.bookingGroup,
        });

        updateGroup(group.id, { status: "active", loading: false });
      }

      setConfirmAction(null);
    } catch (err: any) {
      updateGroup(group.id, { loading: false });
      if (Number(err?.response?.status || 0) === 409) {
        updateGroup(group.id, { status: "locked" });
        setConfirmAction(null);
        return;
      }
      setError(err?.response?.data?.message || t("myBookings.actionError"));
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <header className={styles.header}>
          <h1>{t("myBookings.title")}</h1>
          <p>{t("myBookings.subtitle")}</p>
        </header>

        {loading && <p className={styles.state}>{t("myBookings.loading")}</p>}
        {!loading && error && <p className={styles.state}>{t("myBookings.error", { error })}</p>}
        {!loading && !error && groups.length === 0 && <p className={styles.state}>{t("myBookings.empty")}</p>}

        {!loading && !error && groups.length > 0 && (
          <section className={styles.list}>
            {groups.map((group) => {
              const totalPrice = group.dates.reduce((sum, dateItem) => sum + (dateItem.priceByDay || 0), 0);
              const totalPriceLabel = formatMoney(totalPrice, group.currency, locale);
              const nightsCount = group.dates.length;
              const hasExpiredDate = group.dates.some((entry) => entry.date < todayDateKey);
              const isDisabled = group.loading || group.status === "locked" || group.status === "validated" || hasExpiredDate;
              const btnClassName = `${styles.actionBtn} ${
                hasExpiredDate
                  ? styles.actionBtnExpired
                  : group.status === "validated"
                    ? styles.actionBtnValidated
                    : group.status === "locked"
                      ? styles.actionBtnLocked
                      : group.status === "cancelled"
                        ? styles.actionBtnRebook
                        : styles.actionBtnCancel
              }`;
              const actionLabel =
                group.status === "locked"
                  ? t("myBookings.tooLate")
                  : group.status === "validated"
                    ? t("myBookings.validatedByOwner")
                    : group.status === "cancelled"
                      ? t("myBookings.rebook")
                      : t("myBookings.cancel");

              return (
                <article key={group.id} className={styles.item}>
                  <Link to={`/ads/${group.adId}`} className={styles.mediaLink}>
                    <div className={styles.photoWrap}>
                      <ShimmerImage className={styles.photo} src={group.image} alt={group.title} />
                    </div>
                  </Link>

                  <div className={styles.content}>
                    <h3>
                      <Link to={`/ads/${group.adId}`} className={styles.titleLink}>
                        {group.title}
                      </Link>
                    </h3>
                    <p className={styles.description}>
                      <Link to={`/ads/${group.adId}`} className={styles.descriptionLink}>
                        {group.description || t("myBookings.noDescription")}
                      </Link>
                    </p>

                    <div className={styles.datesPanel}>
                      <div className={styles.dateTags}>
                        {group.dates.map((dateItem) => (
                          <span key={`${group.id}-${dateItem.date}`} className={styles.dateTag}>
                            {dateItem.date}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className={styles.sideActions}>
                    <button
                      type="button"
                      className={btnClassName}
                      onClick={() => requestAction(group.id)}
                      disabled={isDisabled}
                    >
                      {actionLabel}
                    </button>
                    <p className={styles.totalPriceLabel}>
                      {t("myBookings.totalPrice", { price: totalPriceLabel })}
                    </p>
                    <p className={styles.totalNightsLabel}>
                      {t("myBookings.totalNights", { count: nightsCount })}
                    </p>
                  </div>
                </article>
              );
            })}
            <div ref={sentinelRef} className={styles.sentinel} />
            {loadingMore && <p className={styles.state}>{t("myBookings.loadingMoreBookings")}</p>}
          </section>
        )}
      </div>

      {confirmAction && (
        <div className={styles.popupOverlay} onClick={() => setConfirmAction(null)}>
          <div className={styles.popup} onClick={(event) => event.stopPropagation()}>
            <h3>
              {confirmAction.mode === "cancel"
                ? t("myBookings.confirmCancelTitle")
                : t("myBookings.confirmRebookTitle")}
            </h3>
            <p>
              {confirmAction.mode === "cancel"
                ? t("myBookings.confirmCancelText")
                : t("myBookings.confirmRebookText")}
            </p>
            <div className={styles.popupActions}>
              <button type="button" className={styles.popupConfirm} onClick={executeConfirmedAction}>
                {t("myBookings.confirm")}
              </button>
              <button type="button" className={styles.popupCancel} onClick={() => setConfirmAction(null)}>
                {t("myBookings.keep")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyBookingsPage;
